// Orders.jsx
// Shows the logged-in user's past orders fetched from Firestore with a professional UI.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../utils/pricing";
import { shortOrderId, formatDate, statusBadgeClass } from "../utils/helpers";
import { motion, AnimatePresence } from "framer-motion";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function Orders() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchOrders() {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Primary query with server-side ordering
        // NOTE: This requires a composite index (userId: desc, createdAt: desc)
        const q = query(
          collection(db, "orders"),
          where("userId", "==", currentUser.uid),
          orderBy("createdAt", "desc")
        );

        try {
          const snapshot = await getDocs(q);
          setOrders(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (innerErr) {
          // FALLBACK: If index is missing, fetch all and sort in memory
          console.warn("Index might be missing, falling back to client-side sorting:", innerErr);
          const fallbackQ = query(
            collection(db, "orders"),
            where("userId", "==", currentUser.uid)
          );
          const snapshot = await getDocs(fallbackQ);
          const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

          // Sort manually by createdAt (handling different date formats)
          data.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB - dateA;
          });

          setOrders(data);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("We couldn't load your order history. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchOrders();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8 animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg w-48"></div>
          <div className="h-4 bg-gray-100 rounded w-24"></div>
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <div className="flex justify-between items-start mb-6">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-32"></div>
                  <div className="h-3 bg-gray-50 rounded w-24"></div>
                </div>
                <div className="h-6 bg-gray-100 rounded-full w-20"></div>
              </div>
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-gray-50 rounded-xl"></div>
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-4 bg-gray-50 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-50 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <InfoOutlinedIcon sx={{ fontSize: 40 }} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h2>
        <p className="text-gray-500 mb-8">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 text-white px-8 py-3 rounded-2xl font-semibold hover:bg-red-700 transition-all shadow-lg shadow-red-100 active:scale-95"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-24 h-24 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-8 transform -rotate-6">
            <ShoppingBagOutlinedIcon sx={{ fontSize: 48 }} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-display">No orders yet</h2>
          <p className="text-gray-500 mb-10 max-w-sm mx-auto leading-relaxed">
            It looks like you haven't placed any orders yet. Explore our collection of unique gifts!
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-600 to-pink-600 text-white px-10 py-4 rounded-2xl font-bold hover:shadow-xl hover:shadow-rose-100 transition-all active:scale-95"
          >
            Start Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="text-4xl font-extrabold text-gray-900 font-display tracking-tight">Order History</h1>
          <p className="text-gray-500 mt-2 font-medium">Manage and track your gift deliveries</p>
        </div>
        <div className="text-sm font-semibold text-gray-400 uppercase tracking-widest">
          {orders.length} {orders.length === 1 ? 'Order' : 'Orders'} Total
        </div>
      </div>

      <div className="space-y-8">
        <AnimatePresence>
          {orders.map((order, idx) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group"
            >
              {/* Order Header / Bar */}
              <div className="bg-gray-50/50 px-6 py-4 flex flex-wrap items-center justify-between gap-4 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Order ID</span>
                    <p className="font-mono text-sm font-bold text-gray-700">{shortOrderId(order.id)}</p>
                  </div>
                  <div className="w-px h-8 bg-gray-200 hidden sm:block"></div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Date</span>
                    <p className="text-sm font-semibold text-gray-600">{formatDate(order.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-sm ${statusBadgeClass(order.status)}`}>
                    {order.status || 'Pending'}
                  </span>
                </div>
              </div>

              {/* Order Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                  {/* Items List */}
                  <div className="md:col-span-8 space-y-4">
                    {order.items?.map((item, i) => (
                      <div key={i} className="flex gap-4 group/item">
                        <div className="relative w-20 h-20 flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-2xl shadow-sm group-hover/item:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=Gift"; }}
                          />
                          {item.quantity > 1 && (
                            <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-[10px] font-bold w-6 h-6 flex items-center justify-center rounded-full border-2 border-white shadow-sm font-mono">
                              {item.quantity}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 py-1">
                          <h4 className="text-sm font-bold text-gray-900 truncate group-hover/item:text-rose-600 transition-colors">
                            {item.name}
                          </h4>
                          <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">
                            {formatCurrency(item.price)} each
                          </p>
                        </div>
                        <div className="text-right py-1">
                          <p className="text-sm font-bold text-gray-900">
                            {formatCurrency(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Footer / Summary */}
                  <div className="md:col-span-4 bg-gray-50/50 rounded-2xl p-5 flex flex-col justify-between">
                    <div className="space-y-3">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400 uppercase tracking-wider font-semibold">Ship To</span>
                        <span className="text-gray-900 font-bold text-right">{order.shipping?.city}, {order.shipping?.state}</span>
                      </div>
                      {order.isCouponApplied && (
                        <>
                          <div className="flex justify-between text-[10px] border-t border-gray-100 pt-2 opacity-60">
                            <span className="text-gray-400 font-bold uppercase">Original Total</span>
                            <span className="text-gray-500 line-through font-bold">{formatCurrency(order.pricing?.total + order.pricing?.discount)}</span>
                          </div>
                          <div className="flex justify-between text-[11px]">
                            <span className="text-green-600 uppercase tracking-wider font-bold">Coupon</span>
                            <span className="text-green-700 font-black">{order.coupon?.couponCode}</span>
                          </div>
                          <div className="flex justify-between text-[11px]">
                            <span className="text-green-600 uppercase tracking-wider font-bold">Discount</span>
                            <span className="text-green-700 font-black">-{formatCurrency(order.pricing?.discount)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-xs border-t border-gray-100 pt-3">
                        <span className="text-gray-400 uppercase tracking-wider font-semibold">Total</span>
                        <span className="text-xl font-black text-rose-600">{formatCurrency(order.pricing?.total)}</span>
                      </div>
                    </div>

                    <Link
                      to={`/product/${order.items?.[0]?.id}`}
                      className="mt-6 w-full text-center bg-white border border-gray-200 text-gray-700 py-2.5 rounded-xl text-xs font-bold hover:bg-gray-50 hover:border-gray-300 transition-all uppercase tracking-widest shadow-sm"
                    >
                      Buy Again
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
